import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FileText, ChevronLeft, ChevronRight, Eye, X, ListFilter } from "lucide-react";
import { listAllInvoices, viewInvoicePdf } from "../../services/invoiceService";
import { listProjectsLite } from "../../services/projectService";
import ProjectFilterDropdown from "../../components/ProjectFilterDropdown";
import FilterDropdown from "../../components/FilterDropdown";
import "../../assets/css/ProjectList.css";

const STATUS_META = {
  draft:   { bg: "#f1f5f9", color: "#475569", label: "Draft" },
  sent:    { bg: "#eff6ff", color: "#1d4ed8", label: "Sent" },
  partial: { bg: "#fff7ed", color: "#c2410c", label: "Partial" },
  paid:    { bg: "#f0fdf4", color: "#166534", label: "Paid" },
};

const STATUS_FILTER_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "draft", label: "Draft" },
  { value: "sent", label: "Sent" },
  { value: "partial", label: "Partial" },
  { value: "paid", label: "Paid" },
];

const AdminInvoiceList = () => {
  const navigate = useNavigate();
  const itemsPerPage = 20;

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [status, setStatus] = useState("");
  const [projectPid, setProjectPid] = useState("");
  const [issueDateFrom, setIssueDateFrom] = useState("");
  const [issueDateTo, setIssueDateTo] = useState("");
  const [dueDateFrom, setDueDateFrom] = useState("");
  const [dueDateTo, setDueDateTo] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [invoices, setInvoices] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [viewingPid, setViewingPid] = useState(null);

  const [projectOptions, setProjectOptions] = useState([]);

  useEffect(() => {
    listProjectsLite()
      .then((res) => {
        const list = res.data?.response;
        setProjectOptions(Array.isArray(list) ? list : []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, status, projectPid, issueDateFrom, issueDateTo, dueDateFrom, dueDateTo]);

  useEffect(() => {
    const fetchInvoices = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await listAllInvoices({
          page: currentPage,
          limit: itemsPerPage,
          search: debouncedSearch,
          status,
          projectPid,
          issueDateFrom,
          issueDateTo,
          dueDateFrom,
          dueDateTo,
        });
        const data = res.data?.response ?? {};
        const list = Array.isArray(data.items) ? data.items : [];
        setInvoices(list);
        setTotalCount(data.pagination?.total ?? list.length);
      } catch {
        setError("Failed to load invoices.");
      } finally {
        setLoading(false);
      }
    };
    fetchInvoices();
  }, [currentPage, debouncedSearch, status, projectPid, issueDateFrom, issueDateTo, dueDateFrom, dueDateTo]);

  const clearFilters = () => {
    setSearchTerm("");
    setStatus("");
    setProjectPid("");
    setIssueDateFrom("");
    setIssueDateTo("");
    setDueDateFrom("");
    setDueDateTo("");
  };

  const hasActiveFilters = !!(searchTerm || status || projectPid || issueDateFrom || issueDateTo || dueDateFrom || dueDateTo);

  const handleViewPdf = async (inv) => {
    if (!inv.pdfUrl) return;
    setViewingPid(inv.invoicePid);
    try {
      await viewInvoicePdf(inv.pdfUrl);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to open PDF");
    } finally {
      setViewingPid(null);
    }
  };

  const getPaginationItems = (currentPage, totalPages) => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (currentPage <= 4) return [1, 2, 3, 4, 5, "end-ellipsis", totalPages];
    if (currentPage >= totalPages - 3) {
      return [1, "start-ellipsis", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }
    return [1, "start-ellipsis", currentPage - 1, currentPage, currentPage + 1, "end-ellipsis", totalPages];
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));
  const pageStartIndex = (currentPage - 1) * itemsPerPage;
  const paginationItems = getPaginationItems(currentPage, totalPages);

  return (
    <main className="app-wrapper url-list-page">
      <div className="container-fluid">
        <div style={{ marginBottom: 18 }}>
          <h5 style={{ margin: 0, fontWeight: 800, color: "#0f172a" }}>Invoices</h5>
          <p style={{ margin: 0, fontSize: 12, color: "var(--muteColor)" }}>Invoices across all projects</p>
        </div>

        <section className="url-list-toolbar p-3 mb-3">
          <div className="url-list-actions">
            <div className="url-search-field">
              <i className="fa-solid fa-magnifying-glass"></i>
              <input
                type="search"
                className="form-control"
                placeholder="Search invoice #, title, client, sender..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <ProjectFilterDropdown
              projects={projectOptions}
              value={projectPid || "all"}
              onChange={(pid) => setProjectPid(pid === "all" ? "" : pid)}
            />

            <FilterDropdown
              options={STATUS_FILTER_OPTIONS}
              value={status}
              onChange={setStatus}
              icon={ListFilter}
              minWidth={150}
            />
          </div>

          <div className="row g-2 mt-2">
            <div className="col-6 col-md-3">
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--muteColor)" }}>Issue Date From</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={issueDateFrom}
                onChange={(e) => setIssueDateFrom(e.target.value)}
              />
            </div>
            <div className="col-6 col-md-3">
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--muteColor)" }}>Issue Date To</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={issueDateTo}
                onChange={(e) => setIssueDateTo(e.target.value)}
              />
            </div>
            <div className="col-6 col-md-3">
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--muteColor)" }}>Due Date From</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={dueDateFrom}
                onChange={(e) => setDueDateFrom(e.target.value)}
              />
            </div>
            <div className="col-6 col-md-3">
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--muteColor)" }}>Due Date To</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={dueDateTo}
                onChange={(e) => setDueDateTo(e.target.value)}
              />
            </div>
          </div>

          {hasActiveFilters && (
            <div className="mt-2">
              <button
                type="button"
                className="btn btn-sm btn-light d-flex align-items-center gap-1"
                style={{ borderRadius: 7, fontSize: 12 }}
                onClick={clearFilters}
              >
                <X size={13} /> Clear Filters
              </button>
            </div>
          )}
        </section>

        <section className="url-table-card">
          {loading ? (
            <div className="py-5 text-center text-muted">Loading...</div>
          ) : error ? (
            <div className="py-5 text-center text-danger">{error}</div>
          ) : invoices.length === 0 ? (
            <div style={{ padding: "48px 20px", textAlign: "center" }}>
              <div style={{ width: 56, height: 56, borderRadius: 14, background: "#f1f5f9", margin: "0 auto 12px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <FileText size={24} color="#94a3b8" strokeWidth={1.5} />
              </div>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: "#334155" }}>No invoices found</p>
              <p style={{ margin: "4px 0 0", fontSize: 12, color: "#94a3b8" }}>Try adjusting your search or filters.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table url-table mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Invoice #</th>
                    <th>Project</th>
                    <th>Client</th>
                    <th>Issue Date</th>
                    <th>Due Date</th>
                    <th>Total</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv, i) => {
                    const st = STATUS_META[inv.status] || STATUS_META.draft;
                    return (
                      <tr key={inv.invoicePid}>
                        <td className="url-index">{pageStartIndex + i + 1}</td>
                        <td style={{ fontWeight: 700, fontSize: 13 }}>{inv.invoiceNumber}</td>
                        <td style={{ fontSize: 13 }}>{inv.project?.projectName || "—"}</td>
                        <td style={{ fontSize: 13 }}>{inv.to?.name || "—"}</td>
                        <td style={{ fontSize: 13 }}>{inv.issueDate || "—"}</td>
                        <td style={{ fontSize: 13 }}>{inv.dueDate || "—"}</td>
                        <td style={{ fontSize: 13, fontWeight: 700 }}>{inv.currencySymbol}{Number(inv.total || 0).toFixed(2)}</td>
                        <td>
                          <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 999, background: st.bg, color: st.color }}>{st.label}</span>
                        </td>
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              style={{ borderRadius: 7 }}
                              onClick={() => handleViewPdf(inv)}
                              disabled={!inv.pdfUrl || viewingPid === inv.invoicePid}
                              title={!inv.pdfUrl ? "PDF not generated yet" : "View PDF"}
                            >
                              <Eye size={13} />
                            </button>
                            <button
                              className="ph-view-btn"
                              onClick={() => navigate(`/admin-invoices/${inv.invoicePid}/view`, { state: { projectPid: inv.project?.projectPid } })}
                            >
                              View <ChevronRight size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {!loading && totalCount > 0 && (
            <div className="url-pagination-wrap">
              <p className="url-pagination-summary mb-0">
                Showing {pageStartIndex + 1}–{Math.min(pageStartIndex + itemsPerPage, totalCount)} of {totalCount}
              </p>
              <nav aria-label="Invoice list pagination">
                <ul className="url-pagination mb-0">
                  <li>
                    <button
                      type="button"
                      className="url-page-btn"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((p) => p - 1)}
                    >
                      <ChevronLeft size={16} />
                    </button>
                  </li>
                  {paginationItems.map((item) => (
                    <li key={item}>
                      {typeof item === "string" ? (
                        <span className="url-page-ellipsis">...</span>
                      ) : (
                        <button
                          type="button"
                          className={`url-page-btn ${item === currentPage ? "active" : ""}`}
                          onClick={() => setCurrentPage(item)}
                        >
                          {item}
                        </button>
                      )}
                    </li>
                  ))}
                  <li>
                    <button
                      type="button"
                      className="url-page-btn"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage((p) => p + 1)}
                    >
                      <ChevronRight size={16} />
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </section>
      </div>
    </main>
  );
};

export default AdminInvoiceList;
